import React, { useState } from 'react';
import {
    Modal, Box, Typography, Button, TextField, Paper, Grid, Checkbox, FormControlLabel,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Select, MenuItem
} from '@mui/material';
import MarkedText from './MarkedText';
import qualitiesString from './QualitiesString';
import getModText from "./GetModText";
import formatSource from "./FormatSource";
import { adjustCredits } from "./Credits";
import { convertVehicleWeapon } from "./GenesysVehicleRules";
import modStateKey from "./ModStateKey";
import getFullBoughtTalents from "./GetFullBoughtTalents";
import withOrdnance, { ordnanceOptions } from "./Ordnance";

const ARCS = ["Fore", "Port", "Starboard", "Aft", "Dorsal", "Ventral"]
const EMPTY_ARCS = { Fore: false, Port: false, Starboard: false, Aft: false, Dorsal: false, Ventral: false, All: false }

const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : [])
const isOn = (v) => v === true || v === "true"
export const arcText = (arcs) => {
    if (!arcs) { return "" }
    if (isOn(arcs.All)) { return "All" }
    return Object.entries(arcs).filter(([, v]) => isOn(v)).map(([k]) => k).join(", ")
}

const tableSx = { maxHeight: "70vh", overflowY: "auto", mt: "5px" }
const installedSx = { ...tableSx, maxHeight: "25vh" }
const headerSx = { mt: "10px", minHeight: "40px" }

const VehicleWeaponModal = ({ open, handleClose, character, handleCharacterChange, vehicle, setOwnedVehicles, setPreview, weapons, attachments = [], qualities, talents, skills }) => {

    const [modalVehicle, setModalVehicle] = useState({ ...vehicle, VehicleWeapons: asArray(vehicle.VehicleWeapons).map((w) => JSON.parse(JSON.stringify(w))) })
    const [selectedAvailable, setSelectedAvailable] = useState(null)
    const [selectedInstalled, setSelectedInstalled] = useState(null)
    const [previewSource, setPreviewSource] = useState(null)
    const [search, setSearch] = useState("")
    const [count, setCount] = useState(1)
    const [location, setLocation] = useState("")
    const [fireArcs, setFireArcs] = useState(EMPTY_ARCS)
    const [price, setPrice] = useState(0)

    const installed = modalVehicle.VehicleWeapons
    const getWeapon = (key) => weapons.find((w) => w.Key === key)
    const sourceOf = (vehicleWeapon) => (vehicleWeapon.Key && getWeapon(vehicleWeapon.Key)) || vehicleWeapon
    const baseOf = (vehicleWeapon) => convertVehicleWeapon(withOrdnance(sourceOf(vehicleWeapon), vehicleWeapon.LoadedOrdnance, weapons))

    // HP and availability
    const getHpUsed = () => {
        const attachmentHP = modalVehicle.Attachments?.reduce((sum, attachment) => sum + (parseInt(attachment.HP) || 0), 0) || 0
        const weaponHP = installed.reduce((sum, weapon) => sum + (weapon.Added ? 1 : 0), 0)
        return attachmentHP + weaponHP
    }
    const hpFree = getHpUsed() < parseInt(modalVehicle.HP)

    const OEWMavailable = () => !!modalVehicle.Attachments?.find((a) => a.Key === "OEWM") && !installed.find((w) => w.Oversized)

    const availableWeapons = weapons.filter(
        (w) => w.Type === "Vehicle" &&
            w.Price &&
            parseInt(w.SizeHigh) >= parseInt(modalVehicle.Silhouette) &&
            parseInt(w.SizeLow) <= parseInt(modalVehicle.Silhouette) + (OEWMavailable() ? 1 : 0) &&
            (!search || w.Name.toLowerCase().includes(search.toLowerCase()))
    ).sort((a, b) => a.Name.localeCompare(b.Name))

    // Built-in modifications
    const builtInAttachments = (vehicleWeapon) => {
        return asArray(sourceOf(vehicleWeapon).BuiltInAttachments?.Key)
            .map((key) => attachments.find((a) => a.Key === key))
            .filter(Boolean)
    }

    const getModPrice = () => {
        const { Bought: boughtTalents } = getFullBoughtTalents(character)
        return boughtTalents.find((t) => t.key === 'GEARHD' || t.data?.Attributes?.HalveModCost === 'true') ? 500 : 1000
    }

    const setModState = (index, attachment, mod, modI, slot, field, checked) => {
        const vehicleWeapon = installed[index]
        const key = modStateKey(vehicleWeapon.Key || "VW", attachment.Key, mod, modI)
        const count = parseInt(Math.abs(mod.Count) || 1)
        const current = vehicleWeapon.ModStates?.[key] || { installed: Array(count).fill(false), failed: Array(count).fill(false) }
        const other = field === "installed" ? "failed" : "installed"
        const next = {
            [field]: current[field].map((v, j) => (j === slot ? checked : v)),
            [other]: current[other].map((v, j) => (j === slot ? false : v)),
        }
        const updated = { ...vehicleWeapon, ModStates: { ...vehicleWeapon.ModStates, [key]: next } }
        if (field === "installed" && mod.FireArc === "All") {
            updated.FiringArcs = { ...(vehicleWeapon.FiringArcs || EMPTY_ARCS), All: checked }
        }
        const wasPaid = current.installed[slot] || current.failed[slot]
        setPrice(price + (checked ? getModPrice() : wasPaid ? -getModPrice() : 0))
        setModalVehicle({ ...modalVehicle, VehicleWeapons: installed.map((w, i) => (i === index ? updated : w)) })
    }

    // Ordnance
    const setLoadedOrdnance = (index, key) => {
        const updated = { ...installed[index] }
        if (key) { updated.LoadedOrdnance = key } else { delete updated.LoadedOrdnance }
        setModalVehicle({ ...modalVehicle, VehicleWeapons: installed.map((w, i) => (i === index ? updated : w)) })
    }

    const OrdnanceSelect = ({ index }) => {
        const options = ordnanceOptions(sourceOf(installed[index]), weapons)
        if (!options.length) { return null }
        return (
            <Grid container alignItems="center" sx={{ mt: "10px" }}>
                <Typography sx={{ marginRight: "10px" }}>Loaded Ordnance:</Typography>
                <Select size="small" displayEmpty value={installed[index].LoadedOrdnance || ""} onChange={(e) => setLoadedOrdnance(index, e.target.value)}>
                    <MenuItem value="">None</MenuItem>
                    {options.map((o) => <MenuItem key={o.Key} value={o.Key}>{o.Name}</MenuItem>)}
                </Select>
            </Grid>
        )
    }

    const ModOptions = ({ index }) => {
        const vehicleWeapon = installed[index]
        return builtInAttachments(vehicleWeapon).filter((a) => a.AddedMods).map((attachment) => (
            <Box key={attachment.Key} sx={{ mt: "10px" }}>
                <Typography>Modification Options:</Typography>
                {asArray(attachment.AddedMods).map((mod, modI) => {
                    const state = vehicleWeapon.ModStates?.[modStateKey(vehicleWeapon.Key || "VW", attachment.Key, mod, modI)]
                    return Array(parseInt(Math.abs(mod.Count) || 1)).fill(0).map((_, slot) => (
                        <Grid container alignItems="center" key={modI + "-" + slot}>
                            <Grid item sx={{ padding: "5px" }}>
                                {mod.MiscDesc && <div dangerouslySetInnerHTML={{ __html: MarkedText.renderer(mod.MiscDesc) }} />}
                                {mod.Key && <div dangerouslySetInnerHTML={{ __html: MarkedText.renderer(getModText(qualities, talents, skills, mod, true)) }} />}
                            </Grid>
                            <Grid item>
                                <FormControlLabel label="Installed" control={
                                    <Checkbox checked={!!state?.installed[slot]} disabled={!!state?.failed[slot]}
                                        onChange={(e) => setModState(index, attachment, mod, modI, slot, "installed", e.target.checked)} />
                                } />
                            </Grid>
                            <Grid item>
                                <FormControlLabel label="Failed" control={
                                    <Checkbox checked={!!state?.failed[slot]} disabled={!!state?.installed[slot]}
                                        onChange={(e) => setModState(index, attachment, mod, modI, slot, "failed", e.target.checked)} />
                                } />
                            </Grid>
                        </Grid>
                    ))
                })}
            </Box>
        ))
    }

    // Install actions
    const buildWeapon = (weapon) => {
        const n = Math.max(parseInt(count) || 1, 1)
        const newWeapon = JSON.parse(JSON.stringify(weapon))
        if (location) { newWeapon.Location = location }
        if (n > 1) { newWeapon.Qualities = [...asArray(newWeapon.Qualities), { Key: "LINKED", Count: n - 1 }] }
        if (parseInt(newWeapon.SizeLow) > parseInt(modalVehicle.Silhouette)) { newWeapon.Oversized = true }
        newWeapon.FiringArcs = { ...fireArcs }
        setPrice(price + parseInt(newWeapon.Price) + (newWeapon.Price * (n - 1) * .5))
        return newWeapon
    }

    const handleAddWeapon = () => {
        const newWeapon = { ...buildWeapon(selectedAvailable), Added: true }
        setModalVehicle({ ...modalVehicle, VehicleWeapons: [...installed, newWeapon] })
        if (newWeapon.Oversized) { setSelectedAvailable(null); setPreviewSource(null) }
    }

    const handleReplaceWeapon = () => {
        const newWeapon = buildWeapon(selectedAvailable)
        if (installed[selectedInstalled].Added) { newWeapon.Added = true }
        setModalVehicle({ ...modalVehicle, VehicleWeapons: installed.map((w, i) => (i === selectedInstalled ? newWeapon : w)) })
        if (newWeapon.Oversized) { setSelectedAvailable(null); setPreviewSource("installed") }
    }

    const handleRemoveWeapon = () => {
        setModalVehicle({ ...modalVehicle, VehicleWeapons: installed.filter((_, i) => i !== selectedInstalled) })
        setSelectedInstalled(null)
        setPreviewSource(selectedAvailable ? "available" : null)
    }

    const handleSubmit = () => {
        const newVehicle = { ...modalVehicle }
        const newCategory = [...character.Vehicles]
        newCategory[character.Vehicles.findIndex((v) => v.inventoryID === vehicle.inventoryID)] = newVehicle
        setOwnedVehicles(newCategory)
        handleCharacterChange({
            Vehicles: newCategory,
            Credits: adjustCredits(character.Credits, -price)
        });
        setPrice(0)
        setPreview(newVehicle)
    };

    // Preview panel
    const ModLines = ({ weapon }) => asArray(weapon.BaseMods).map((mod, index) => (
        <div key={(mod.Key || "") + index}>
            {mod.MiscDesc && <div dangerouslySetInnerHTML={{ __html: MarkedText.renderer(mod.MiscDesc) }} />}
            {mod.Key && <div dangerouslySetInnerHTML={{ __html: MarkedText.renderer(getModText(qualities, talents, skills, mod)) }} />}
        </div>
    ))

    const weaponDetails = (vehicleWeapon) => {
        const weapon = baseOf(vehicleWeapon)
        const props = vehicleWeapon.VehicleWeaponProperties || vehicleWeapon
        const qualitiesList = qualitiesString(vehicleWeapon.Added && !vehicleWeapon.LoadedOrdnance ? [] : weapon.Qualities || [], props)
        const n = parseInt(props.Count) || 1
        return (
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Typography variant="h6">{(n > 1 ? n + " " : "") + weapon.Name + (props.Turret ? " Turret" : "") + (n > 1 ? "s" : "")}</Typography>
                    {weapon.Description && <div dangerouslySetInnerHTML={{ __html: MarkedText.renderer(weapon.Description) }} />}
                    {weapon.Source && <Typography variant="caption">{formatSource(weapon.Source)}</Typography>}
                </Grid>
                <Grid item xs={12} md={6}>
                    {weapon.LoadedOrdnanceName && <Typography>Loaded: {weapon.LoadedOrdnanceName}</Typography>}
                    <Typography>Damage: {weapon.Damage || '-'}</Typography>
                    <Typography>Crit: {weapon.Crit || '-'}</Typography>
                    <Typography>Range: {weapon.Range}</Typography>
                    <Typography>Silhouette: {weapon.SizeLow ?? '-'} to {weapon.SizeHigh ?? '-'}</Typography>
                    <Typography>Price: {weapon.Price ?? '-'}</Typography>
                    <Typography>Rarity: {weapon.Rarity ?? '-'}</Typography>
                    {previewSource === "installed" && <>
                        <Typography>Fire Arc: {arcText(props.FiringArcs) || '-'}</Typography>
                        {props.Location && <Typography>Location: {props.Location}</Typography>}
                        {isOn(props.Retractable) && <Typography>Retractable</Typography>}
                        {props.Scale === "wsPersonal" && <Typography>Personal Scale</Typography>}
                        {vehicleWeapon.Oversized && <Typography>Oversized</Typography>}
                        {!vehicleWeapon.Added && <Typography variant="caption">Stock weapon</Typography>}
                    </>}
                    {qualitiesList && qualitiesList !== "Qualities: " && <Typography>{qualitiesList}</Typography>}
                    <Typography component="div" variant="body2"><ModLines weapon={weapon} /></Typography>
                </Grid>
            </Grid>
        )
    }

    const installOptions = () => (
        <>
            <Grid container alignItems="center" spacing={2} sx={{ paddingTop: "10px" }}>
                <Grid item>
                    <TextField size="small" label="Location" value={location} onChange={(e) => setLocation(e.target.value)} />
                </Grid>
                <Grid item>
                    <TextField
                        size="small"
                        label="Count"
                        sx={{ maxWidth: "70px" }}
                        inputProps={{ style: { textAlign: 'center' } }}
                        value={count}
                        onChange={(e) => { if (!isNaN(e.target.value)) { setCount(e.target.value) } }}
                    />
                </Grid>
            </Grid>
            <Grid container alignItems="center">
                <Typography sx={{ marginRight: "10px" }}>Firing Arcs:</Typography>
                <FormControlLabel label="All" control={<Checkbox checked={fireArcs.All} onChange={() => setFireArcs({ ...fireArcs, All: !fireArcs.All })} />} />
                {ARCS.map((arc) => (
                    <FormControlLabel key={arc} label={arc} control={
                        <Checkbox disabled={fireArcs.All} checked={fireArcs.All || fireArcs[arc]} onChange={() => setFireArcs({ ...fireArcs, [arc]: !fireArcs[arc] })} />
                    } />
                ))}
            </Grid>
            <Button disabled={!hpFree} onClick={handleAddWeapon}>Add</Button>
            {selectedInstalled !== null && installed[selectedInstalled] &&
                <Button onClick={handleReplaceWeapon}>Replace {baseOf(installed[selectedInstalled]).Name}</Button>
            }
            {!hpFree && <Typography variant="caption">No free hard points</Typography>}
        </>
    )

    const previewWeapon = previewSource === "installed" ? installed[selectedInstalled] : previewSource === "available" ? selectedAvailable : null

    return (
        <Modal open={open} onClose={handleClose}>
            <Paper sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                padding: "25px",
                width: "100%",
                maxHeight: "100vh",
                overflowY: "auto",
            }}>
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item>
                        <Typography variant="h6">{modalVehicle.Name} Weapons</Typography>
                        <Typography>HP Used: {getHpUsed()} / {modalVehicle.HP}</Typography>
                    </Grid>
                    <Grid item>
                        <Button onClick={() => handleClose()}>🞫</Button>
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Grid container justifyContent="space-between" alignItems="center" sx={headerSx}>
                            <Typography>Available Weapons</Typography>
                            <TextField size="small" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
                        </Grid>
                        <TableContainer component={Paper} variant="outlined" sx={tableSx}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell align="center">Sil</TableCell>
                                        <TableCell align="center">Dam</TableCell>
                                        <TableCell align="center">Crit</TableCell>
                                        <TableCell align="center">Range</TableCell>
                                        <TableCell align="right">Price</TableCell>
                                        <TableCell align="right">Rarity</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {availableWeapons.map((weapon) => {
                                        const w = convertVehicleWeapon(weapon)
                                        return (
                                            <TableRow
                                                key={weapon.Key}
                                                hover
                                                selected={selectedAvailable?.Key === weapon.Key}
                                                sx={{ cursor: "pointer" }}
                                                onClick={() => { setSelectedAvailable(weapon); setPreviewSource("available") }}
                                            >
                                                <TableCell>{weapon.Name}</TableCell>
                                                <TableCell align="center">{weapon.SizeLow}–{weapon.SizeHigh}</TableCell>
                                                <TableCell align="center">{w.Damage || '-'}</TableCell>
                                                <TableCell align="center">{w.Crit || '-'}</TableCell>
                                                <TableCell align="center">{w.Range}</TableCell>
                                                <TableCell align="right">{weapon.Price}</TableCell>
                                                <TableCell align="right">{weapon.Rarity}</TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Grid container alignItems="center" sx={headerSx}>
                            <Typography>Installed Weapons</Typography>
                        </Grid>
                        <TableContainer component={Paper} variant="outlined" sx={installedSx}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell align="center">Arcs</TableCell>
                                        <TableCell align="center">Dam</TableCell>
                                        <TableCell align="center">Crit</TableCell>
                                        <TableCell align="center">Range</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {installed.map((vw, index) => {
                                        const w = baseOf(vw)
                                        const n = parseInt(vw.Count) || 1
                                        return (
                                            <TableRow
                                                key={index}
                                                hover
                                                selected={selectedInstalled === index}
                                                sx={{ cursor: "pointer" }}
                                                onClick={() => { setSelectedInstalled(index); setPreviewSource("installed") }}
                                            >
                                                <TableCell>{(n > 1 ? n + "× " : "") + (w?.Name || vw.Key) + (vw.Turret ? " Turret" : "")}</TableCell>
                                                <TableCell align="center">{arcText(vw.FiringArcs)}</TableCell>
                                                <TableCell align="center">{w?.Damage || '-'}</TableCell>
                                                <TableCell align="center">{w?.Crit || '-'}</TableCell>
                                                <TableCell align="center">{w?.Range}</TableCell>
                                            </TableRow>
                                        )
                                    })}
                                    {!installed.length && <TableRow><TableCell colSpan={5}>No weapons installed</TableCell></TableRow>}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Grid container alignItems="center" sx={headerSx}>
                            <Typography>Details</Typography>
                        </Grid>
                        <Paper variant="outlined" sx={{ padding: "15px", mt: "5px", maxHeight: "40vh", overflowY: "auto" }}>
                            {!previewWeapon && <Typography>Select a weapon to see its details.</Typography>}
                            {previewWeapon && weaponDetails(previewWeapon)}
                            {previewSource === "installed" && installed[selectedInstalled] && <>
                                <OrdnanceSelect index={selectedInstalled} />
                                <ModOptions index={selectedInstalled} />
                            </>}
                            {previewSource === "available" && selectedAvailable && installOptions()}
                            {previewSource === "installed" && previewWeapon?.Added &&
                                <Button onClick={handleRemoveWeapon}>Remove</Button>
                            }
                        </Paper>
                    </Grid>
                </Grid>
                <Grid container alignItems="center" sx={{ mt: "10px" }}>
                    <Grid item>
                        <Button onClick={() => handleSubmit()}>Save</Button>
                    </Grid>
                    <Grid item>
                        <Typography>Cost: {price}</Typography>
                    </Grid>
                </Grid>
            </Paper>
        </Modal>
    );
};

export default VehicleWeaponModal;
